import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { IBusiness, ICategory, INeighBourhood, ISearchBarData } from '../../state/api/types';
import { NAV_SIZES } from '../../utils/constants';
import { categories, neighbourhoods } from '../../utils/fakeData';
import Presenter from './Presenter';
import { IContainerProps } from './types';
import actions from '../../state/actions';
import { IAppState } from '../../state/types';
import selectors from '../../state/selectors';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import 'moment/locale/es';

const SearchContainer = (props: IContainerProps) => {
    const dispatch = useDispatch();
    const location = useLocation();

    // Defensive handling for navigation state to prevent crashes on direct navigation
    const defaultSearchData: ISearchBarData = { business: '', date: '', location: '' };
    const locationState = location.state as { searchData?: ISearchBarData; selectedCat?: ICategory[] } | null;
    const searchBarData: ISearchBarData = locationState?.searchData || defaultSearchData;
    const selectedCategories: ICategory[] = locationState?.selectedCat || [];

    const [bussinessClicked, setBussinessClicked] = useState<string>('');
    const [isBigNavVisible, setBigNavVisibility] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // SearchBar state management (same as Home)
    const [formData, setFormData] = useState<ISearchBarData>(searchBarData);
    const [selectedCategoriesState, setSelectedCategoriesState] = useState<ICategory[]>(selectedCategories);
    const [isBtnDisabled] = useState<boolean>(false);

    // Categories
    const [searchResults, setSearchResults] = useState<ICategory[]>(categories);
    const [isCategoriesBoxVisible, setCategoriesBoxVisibility] = useState(false);
    const toggleCategoriesBoxVisibility = (state?: boolean) => setCategoriesBoxVisibility(state ?? !isCategoriesBoxVisible);

    // Date
    const [isDatePickerVisible, setDatePickerVisibility] = useState(false);
    const toggleDatePickerVisibility = (state?: boolean) => setDatePickerVisibility(state ?? !isDatePickerVisible);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>();
    const handleSelectDate = (date?: Date) => {
        setFormData({ ...formData, date: moment(date).locale('es').format('DD MMMM') ?? '' });
        setSelectedDate(date);
        setCategoriesBoxVisibility(false);
    };

    // Location
    const [searchHoodResults, setSearchHoodResults] = useState<INeighBourhood[]>(neighbourhoods);
    const [isLocationBoxVisible, setLocationBoxVisibility] = useState(false);
    const toggleLocationBoxVisibility = (state?: boolean) => setLocationBoxVisibility(state ?? !isLocationBoxVisible);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
        let inputData = e.target.value;
        setFormData({ ...formData, [type]: inputData });
    };

    const searchBusinessCategories = () => {
        if (formData.business.length > 0) {
            let _results = categories.filter(category => category.name.toUpperCase().includes(formData.business.toUpperCase()));
            setSearchResults(_results);
        } else {
            setSearchResults(categories);
        }
    };

    const addCategory = (category: ICategory) => {
        if (!selectedCategoriesState.find(c => c.id === category.id)) {
            setSelectedCategoriesState([...selectedCategoriesState, category]);
        }
    };

    const removeCategory = (category: ICategory) => {
        setSelectedCategoriesState(selectedCategoriesState.filter(c => c.id !== category.id));
    };

    const searchLocations = () => {
        if (formData.location.length > 0) {
            let _results = neighbourhoods.filter(item => item.name.toUpperCase().includes(formData.location.toUpperCase()));
            setSearchHoodResults(_results);
        } else {
            setSearchHoodResults(neighbourhoods);
        }
    };

    const selectLocation = (location: INeighBourhood) => {
        setFormData({ ...formData, location: location.name });
        setLocationBoxVisibility(false);
    };

    // Update search when form data or categories change
    useEffect(() => {
        searchBusinessCategories();
    }, [formData.business]);

    useEffect(() => {
        searchLocations();
    }, [formData.location]);

    const allBusinesses = useSelector((state: IAppState) => selectors.api.getBusinesses(state));

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        dispatch(actions.api.getBusinesses());
        // Simulate loading completion when businesses are available
        if (allBusinesses && allBusinesses.length >= 0) {
            setIsLoading(false);
        }
    }, []);

    // Filter businesses based on search criteria (use state values for live filtering)
    const filteredBusinesses = useMemo(() => {
        if (!allBusinesses || allBusinesses.length === 0) {
            return [];
        }

        return allBusinesses.filter((business: IBusiness) => {
            // Filter by selected categories (compare by NAME, case-insensitive)
            // Check both categories array AND businessType for matches
            if (selectedCategoriesState && selectedCategoriesState.length > 0) {
                const hasMatchingCategory = business.categories?.some((cat: ICategory) =>
                    selectedCategoriesState.some((selected: ICategory) =>
                        selected.name.toLowerCase() === cat.name.toLowerCase()
                    )
                );
                // businessType can be string or object { name, id } from backend
                const businessTypeName = typeof business.businessType === 'object'
                    ? (business.businessType as any)?.name
                    : business.businessType;
                const hasMatchingBusinessType = businessTypeName &&
                    selectedCategoriesState.some((selected: ICategory) =>
                        selected.name.toLowerCase() === businessTypeName.toLowerCase()
                    );
                if (!hasMatchingCategory && !hasMatchingBusinessType) {
                    return false;
                }
            }

            // Filter by location (city or address)
            if (formData.location && formData.location.trim() !== '') {
                const locationLower = formData.location.toLowerCase();
                const cityMatch = business.fullAddress?.city?.toLowerCase().includes(locationLower);
                const address1Match = business.fullAddress?.address1?.toLowerCase().includes(locationLower);
                const address2Match = business.fullAddress?.address2?.toLowerCase().includes(locationLower);

                if (!cityMatch && !address1Match && !address2Match) {
                    return false;
                }
            }

            // Filter by business name
            if (formData.business && formData.business.trim() !== '') {
                const businessNameMatch = business.name?.toLowerCase().includes(formData.business.toLowerCase());
                if (!businessNameMatch) {
                    return false;
                }
            }

            return true;
        });
    }, [allBusinesses, selectedCategoriesState, formData]);

    // Update loading state when businesses change
    useEffect(() => {
        if (allBusinesses && allBusinesses.length >= 0) {
            setIsLoading(false);
        }
    }, [allBusinesses]);

    const handleBusinessClicked = (businessIndex: string) => {
        setBussinessClicked(businessIndex);
    }

    const changeNavbar = (size: NAV_SIZES) => {
        setBigNavVisibility(true);
    }

    return (
        <Presenter
            {...props}
            businesses={filteredBusinesses}
            bussinessClicked={bussinessClicked}
            handleBusinessClicked={handleBusinessClicked}
            searchBarData={formData}
            selectedCategories={selectedCategoriesState}
            changeNavbar={changeNavbar}
            isBigNavVisible={isBigNavVisible}
            isLoading={isLoading}
            error={error}
            // SearchBar props
            formData={formData}
            onChange={onChange}
            isBtnDisabled={isBtnDisabled}
            searchResults={searchResults}
            isCategoriesBoxVisible={isCategoriesBoxVisible}
            toggleCategoriesBoxVisibility={toggleCategoriesBoxVisibility}
            searchBusinessCategories={searchBusinessCategories}
            addCategory={addCategory}
            removeCategory={removeCategory}
            isDatePickerVisible={isDatePickerVisible}
            toggleDatePickerVisibility={toggleDatePickerVisibility}
            selectedDate={selectedDate}
            handleSelectDate={handleSelectDate}
            isLocationBoxVisible={isLocationBoxVisible}
            toggleLocationBoxVisibility={toggleLocationBoxVisibility}
            searchLocations={searchLocations}
            searchHoodResults={searchHoodResults}
            selectLocation={selectLocation}
        />
    );
};

export default SearchContainer;
